import { featureFlagsQuery, isFeatureEnabled } from "@common/api/feature-flags"
import { erpConfigQuery } from "@common/api/integrations"
import { Button } from "@common/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@common/components/ui/sheet"
import { REPORT_TEMPLATE } from "@common/flags"
import { useCurrentUser } from "@common/hooks/use-permissions"
import { hasPermission } from "@common/lib/permissions"
import { cn } from "@common/lib/utils"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, Outlet } from "@tanstack/react-router"
import { Menu } from "lucide-react"
import { useState } from "react"
import { SecondaryPanel } from "@/components/layout/secondary-panel"
import { NOTIFICATIONS_PERMISSION } from "@/routes/_layout/settings/_panel/account/notifications/-lib/permission"
import { PROFILE_PERMISSION } from "@/routes/_layout/settings/_panel/account/profile/-lib/permission"
import { SIGNING_PIN_PERMISSION } from "@/routes/_layout/settings/_panel/account/signing-pin/-lib/permission"
import { hasCorridorErp } from "@/routes/_layout/settings/_panel/integrations/erp/-lib/corridor"
import { APPROVAL_FLOWS_PERMISSION } from "@/routes/_layout/settings/_panel/workspace/approval-flows/-lib/permission"
import { MEMBERS_PERMISSION } from "@/routes/_layout/settings/_panel/workspace/members/-lib/permission"
import { NOTIFICATION_RULES_PERMISSION } from "@/routes/_layout/settings/_panel/workspace/notification-rules/-lib/permission"
import { TEMPLATES_PERMISSION } from "@/routes/_layout/settings/_panel/workspace/templates/-lib/kinds"

const SECTIONS = [
  {
    label: "Account",
    links: [
      {
        label: "Profile",
        to: "/settings/account/profile",
        permission: PROFILE_PERMISSION,
      },
      {
        label: "Notifications",
        to: "/settings/account/notifications",
        permission: NOTIFICATIONS_PERMISSION,
      },
      {
        label: "Signing PIN",
        to: "/settings/account/signing-pin",
        permission: SIGNING_PIN_PERMISSION,
      },
    ],
  },
  {
    label: "Workspace",
    links: [
      { label: "General", to: "/settings/workspace/general" },
      {
        label: "Members",
        to: "/settings/workspace/members",
        permission: MEMBERS_PERMISSION,
      },
      {
        label: "Roles",
        to: "/settings/workspace/roles",
        permission: "roles:read",
      },
      {
        label: "Departments",
        to: "/settings/workspace/departments",
        permission: "department:read",
      },
      {
        label: "Teams",
        to: "/settings/workspace/teams",
        permission: "team:read",
      },
      {
        label: "Facilities",
        to: "/settings/workspace/facilities",
        permission: "facility:read",
      },
      {
        label: "Tool categories",
        to: "/settings/workspace/tool-categories",
        permission: "tools:read",
      },
      {
        label: "Notification rules",
        to: "/settings/workspace/notification-rules",
        permission: NOTIFICATION_RULES_PERMISSION,
      },
      {
        label: "Approval flows",
        to: "/settings/workspace/approval-flows",
        permission: APPROVAL_FLOWS_PERMISSION,
      },
      {
        label: "Templates",
        to: "/settings/workspace/templates",
        permission: TEMPLATES_PERMISSION,
        flag: REPORT_TEMPLATE,
      },
    ],
  },
  {
    label: "Quoting",
    links: [
      {
        label: "General",
        to: "/settings/quoting/general",
        permission: "quote_settings:read",
      },
      {
        label: "Customer approval",
        to: "/settings/quoting/customer-approval",
        permission: "quote_settings:read",
      },
      {
        label: "Status",
        to: "/settings/quoting/status",
        permission: "quote_disposition:read",
      },
      {
        label: "Task groups",
        to: "/settings/quoting/task-groups",
        permission: "quote_task_group:read",
      },
      {
        label: "Rate cards",
        to: "/settings/quoting/rate-cards",
        permission: "rate_card:read",
      },
      {
        label: "Tax rates",
        to: "/settings/quoting/tax-rates",
        permission: "tax_rate:read",
      },
      {
        label: "Playbooks",
        to: "/settings/quoting/playbooks",
        permission: "quote_ruleset:read",
      },
    ],
  },
  {
    label: "Work orders",
    links: [
      {
        label: "General",
        to: "/settings/work-orders/general",
        permission: "work_package_settings:read",
      },
    ],
  },
  {
    label: "Integrations",
    links: [
      {
        label: "CRM",
        to: "/settings/integrations/crm",
        permission: "tenant_integration_config:read",
      },
      {
        label: "ERP",
        to: "/settings/integrations/erp",
        permission: "erp_config:read",
        requiresCorridorErp: true,
      },
    ],
  },
] as const

const TITLE = cn(
  "flex h-10 shrink-0 items-center border-b px-4",
  "text-body-semibold text-foreground",
)

const GROUP_LABEL = cn(
  "flex h-9 shrink-0 items-end px-2 pb-1.5",
  "text-extra-small-regular text-greyscale-mid-grey",
)

const LINK = cn(
  "flex h-7 shrink-0 items-center rounded-md px-2",
  "text-body-regular text-sidebar-foreground transition-colors hover:bg-sidebar-accent hover:no-underline",
  "data-[status=active]:bg-sidebar-accent",
)

const MOBILE_BAR = cn("flex items-center px-4 py-3 lg:hidden", "border-b")

export const Route = createFileRoute("/_layout/settings/_panel")({
  staticData: { breadcrumb: "Settings", secondarySidebar: true },
  component: SettingsLayout,
})

function SettingsLayout(): React.ReactElement {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <>
      <SecondaryPanel>
        <SettingsNav />
      </SecondaryPanel>
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetContent
          side="left"
          className="w-full max-w-none p-0 sm:max-w-none md:w-72"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Settings</SheetTitle>
          </SheetHeader>
          <SettingsNav onNavigate={() => setMobileMenuOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <div className={MOBILE_BAR}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="h-4 w-4" />
            Settings
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </div>
    </>
  )
}

function SettingsNav({
  onNavigate,
}: {
  onNavigate?: () => void
}): React.ReactElement {
  const user = useCurrentUser()
  const { data: erpConfig } = useQuery({
    ...erpConfigQuery(),
    enabled: hasPermission(user, "erp:read"),
  })
  const corridorErp = erpConfig !== undefined && hasCorridorErp(erpConfig)
  const { data: flags } = useQuery(featureFlagsQuery())
  const sections = SECTIONS.map((section) => ({
    label: section.label,
    links: section.links.filter(
      (link) =>
        (!("permission" in link) || hasPermission(user, link.permission)) &&
        (!("requiresCorridorErp" in link) || corridorErp) &&
        (!("flag" in link) ||
          (flags !== undefined && isFeatureEnabled(flags, link.flag))),
    ),
  })).filter((section) => section.links.length > 0)

  return (
    <div className="flex h-full flex-col">
      <div className={TITLE}>Settings</div>
      <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
        {sections.map((section) => (
          <div key={section.label} className="flex flex-col gap-px">
            <div className={GROUP_LABEL}>{section.label}</div>
            {section.links.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={onNavigate}
                className={LINK}
              >
                {link.label}
              </Link>
            ))}
          </div>
        ))}
      </nav>
    </div>
  )
}
